import { DEFAULT_LOCALE } from '@/constants';
import { formatCompactParts } from '@/utils/format';

const getPrefix = ({ maxRate, formatMessage, calcRate }) =>
  maxRate !== null
    ? formatMessage({ id: 'rate.display.to' }, { rate: maxRate })
    : formatMessage({ id: 'rate.display.from' }, { rate: calcRate });

const getPeriodicalRateCollectionList = ({
  periodicalRateCollection,
  formatMessage,
}) =>
  periodicalRateCollection?.map(({ month, calcRate, maxRate, uird, rate }) => {
    const displayRate = maxRate !== null ? maxRate : calcRate;
    const prefixText =
      maxRate !== null
        ? formatMessage({ id: 'rate.display.to' }, { rate: displayRate })
        : formatMessage({ id: 'rate.display.from' }, { rate: displayRate });

    return {
      text: formatMessage({ id: 'rate.periodical.prefix' }, { month }),
      value: formatMessage(
        { id: 'rate.periodical.value' },
        { prefix: prefixText }
      ),
      subline: formatMessage({ id: 'rate.periodical.subline' }, { uird, rate }),
    };
  });

export function getMinMaxDataCurrentText({
  data,
  formatMessage,
  withRange,
  shortNumbers = true,
  key = 'rate.display.fallback',
  locale = DEFAULT_LOCALE,
}) {
  if (!data || typeof data !== 'object') return null;

  const { min, max } = data;

  if (min && max && withRange) {
    return formatMessage({ id: `${key}.fromTo` }, { min, max });
  }

  if (min) {
    return formatMessage(
      { id: `${key}.from` },
      {
        ...(shortNumbers
          ? formatCompactParts(min, locale)
          : { numberPart: min }),
      }
    );
  }

  if (max) {
    return formatMessage(
      { id: `${key}.to` },
      {
        ...(shortNumbers
          ? formatCompactParts(max, locale)
          : { numberPart: max }),
      }
    );
  }

  return null;
}

export function getRateDisplay({
  loanRateYear,
  baseCalculatedRate,
  calculatedRateLimit,
  standardUird,
  periodicalRateCollection = [],
  formatMessage,
}) {
  // Періодичні ставки
  if (periodicalRateCollection?.length) {
    const [first] = periodicalRateCollection;
    const { calcRate, maxRate, uird } = first;

    const prefix = getPrefix({ maxRate, formatMessage, calcRate });

    const m = +(calcRate - uird).toFixed(2);

    const tooltip =
      maxRate !== null
        ? formatMessage(
            { id: 'rate.tooltip.withLimit' },
            { n: uird, m, limit: maxRate }
          )
        : formatMessage({ id: 'rate.tooltip.noLimit' }, { n: uird, m });

    return {
      type: 'periodicalRate',
      rate: maxRate !== null ? maxRate : calcRate,
      text: prefix,
      tooltip,
      list: getPeriodicalRateCollectionList({
        periodicalRateCollection,
        formatMessage,
      }),
    };
  }

  // Базова ставка + UIRD
  if (baseCalculatedRate !== null) {
    const prefix =
      calculatedRateLimit !== null
        ? formatMessage(
            { id: 'rate.display.to' },
            { rate: calculatedRateLimit }
          )
        : formatMessage(
            { id: 'rate.display.from' },
            { rate: baseCalculatedRate }
          );

    const m = +(baseCalculatedRate - standardUird).toFixed(2);

    const tooltip =
      calculatedRateLimit !== null
        ? formatMessage(
            { id: 'rate.tooltip.withLimit' },
            { n: standardUird, m, limit: calculatedRateLimit }
          )
        : formatMessage({ id: 'rate.tooltip.noLimit' }, { n: standardUird, m });

    return {
      type: 'baseCalculatedRate',
      rate:
        calculatedRateLimit !== null ? calculatedRateLimit : baseCalculatedRate,
      text: prefix,
      tooltip,
    };
  }

  // Fallback — якщо немає ні базової ставки, ні періодичних ставок
  return {
    type: 'fallback',
    rate: loanRateYear?.min || loanRateYear?.max,
    text: getMinMaxDataCurrentText({
      data: loanRateYear,
      formatMessage,
    }),
    rangeText: getMinMaxDataCurrentText({
      data: loanRateYear,
      formatMessage,
      withRange: true,
    }),
    tooltip: '',
  };
}

export default getRateDisplay;
